import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/shared/lib/utils';

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
	{
		variants: {
			variant: {
				primary:
					'bg-primary text-black font-medium hover:bg-primary-dark  disabled:bg-gray-03 disabled:text-label-tertiary',
				destructive:
					'bg-red text-white hover:bg-red-dark disabled:bg-gray-03 disabled:text-label-tertiary',
				outline:
					'border border-gray-05 text-black bg-background-primary hover:bg-background-secondary hover:text-label-secondary disabled:border-gray-06  disabled:text-label-tertiary',
				link: 'text-label-primary hover:text-label-secondary focus:text-label-secondary',
			},
			size: {
				default: 'h-9 px-4 py-2 has-[>svg]:px-3',
				sm: 'py-1.5 px-2 h-8 has-[>svg]:px-1.5',
				lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
				full: 'px-4 py-3 w-full',
			},
		},
		defaultVariants: {
			variant: 'primary',
			size: 'default',
		},
	}
);

function Button({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<'button'> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot : 'button';

	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	);
}

export { Button, buttonVariants };
